import { BroadcastActions } from '../user/types';

import type { AVRoomState } from './types';

import loggerService from '@/services/logger.service';
import { RootState } from '@/store';

const getters = {
  chatMessages: (state: AVRoomState, g: any, rootState: RootState, rootGetters: any) => (channelIndex: number) => {
    return state.chats[channelIndex].channel.messages;
  },
  chatPinnedMessages:
    (state: AVRoomState, g: any, rootState: RootState, rootGetters: any) => (channelIndex: number) => {
      return state.chats[channelIndex].channel.pinned_messages;
    },
  reactionsForPost: (state: AVRoomState) => (id: string, channel_id: string) => {
    const chat = state.chats.find(chat => chat.channel.id === channel_id);
    if (chat) {
      return chat.channel.messages.find(msg => msg.id === id)?.reactions || {};
    }
    return {};
  },
  isChatLoading: (state: AVRoomState) => (channel_id: string) => {
    const chat = state.chats.find(chat => chat.channel.id === channel_id);
    if (chat) {
      return chat.channel.loadingState === 'loading';
    }
    return false;
  },
  latestChats(state: AVRoomState, getters: any, rootState: RootState, rootGetters: any) {
    return state.chats
      .map(chat => {
        if (chat.channel.type === 'D') {
          const searchFilter = rootState.filterBar.SidebarMessage.searchTerm;
          if (!chat.channel.name) {
            return null;
          }
          let otherUser = rootGetters['mattermost/otherUser'](chat.channel.name.split('__'));

          // Don't show people in chat whose have not attended the event yet
          // otherwise they appear in the sidebar historical chats
          // and not in the people tab
          if (otherUser && otherUser.eventUser && otherUser.eventUser.entered === null) {
            otherUser = null;
          }

          try {
            const filterMatch = new RegExp(searchFilter, 'i').test(otherUser?.name);
            if (!otherUser || !filterMatch) {
              return null;
            }
          } catch (error) {
            return null;
          }

          const response: any = {
            id: chat.channel.id,
            roomId: chat.channel.id,
            user: otherUser,
            panel: false,
            type: 'direct_message_channel',
            total_unread: chat.channel.total_unread || 0
          };
          if (chat.channel.messages.length > 0) {
            const message = chat.channel.messages[chat.channel.messages.length - 1];
            response.text = message.text;
            response.createdAt = new Date(message.create_at).toString();
            response.lastMessage = new Date(message.create_at);
          } else {
            response.text = '';
            response.createdAt = new Date().toString();
            response.lastMessage = new Date(1, 1, 2000); // To make it appear at the end put an old date
          }
          return response;
        }
        return null;
      })
      .filter(it => !!it)
      .sort((a: any, b: any) => b?.lastMessage - a?.lastMessage);
  },
  goldBotState(state: AVRoomState, getters: any, rootState: RootState) {
    const { quality_rating: qualityRating = 0, demo_requested: demoRequested } =
      rootState.user.currentEventUser?.eventUser || {};
    const botConfig: any = {
      qualityRating,
      demoRequested,
      validForDemo: qualityRating > 3
    };
    return botConfig;
  },
  publishedPollData(state: AVRoomState, getters: any, rootState: RootState, rootGetters: any) {
    if (state.polls?.details?.questions) {
      let pollLoadingState = state.channel_loading[state.polls.id];
      const mmsocketstatus = rootState.mattermost.socketstatus;
      if (mmsocketstatus === 'offline' || mmsocketstatus === 'error') {
        pollLoadingState = 'loading';
      }

      if (!pollLoadingState) {
        loggerService.warn('Did not find any broadcast channels for poll to show published poll data!');
        pollLoadingState = 'loading';
      }

      const poll = state.polls.details.questions[state.polls.details.currentPublishedPollID];
      const hasAdminAccess = rootGetters['event/isBroadcastSpeakerOrOrganizer'] && !rootState.event.stage?.isTVMode;

      if (pollLoadingState === 'loading') {
        return null;
      }

      return (
        poll && {
          ...poll,
          open: !rootState.event.stage?.isTVMode && !hasAdminAccess && poll?.id === getters.openedPollData?.id,
          hasAdminAccess,
          isAnswerLoading: getters['isAnyAnswerLoading'],
          answers: Object.values(poll.answers).reduce((acc, answer) => {
            return {
              ...acc,
              [answer.id]: {
                ...answer,
                selected: getters.openedPollData?.votedAnswers?.includes(answer.id)
              }
            };
          }, {})
        }
      );
    }
  },
  openedPollData(state: AVRoomState) {
    if (state.polls?.details?.questions) {
      return state.polls.details.questions[state.polls.details.currentOpenedPollID];
    }
  },
  isAnyAnswerLoading(state: AVRoomState, getters: any) {
    const currentPolledQuestion = getters['openedPollData'];
    if (currentPolledQuestion && currentPolledQuestion.id) {
      return Object.keys(currentPolledQuestion.answers).reduce((isAnyLoading: boolean, answerID): boolean => {
        return isAnyLoading || currentPolledQuestion.answers[answerID].loading;
      }, false);
    }
    return false;
  },
  getIncomingVideoCall(state: AVRoomState) {
    return state.incomingVideoCall;
  },
  getAttendeePrivateRooms(state: AVRoomState) {
    return state.attendeesPrivateDiscussionRooms;
  },
  isChannelLoading: (state: AVRoomState) => (channelMMID: string) => {
    const channelsLoading = state?.channel_loading;
    if (channelMMID && channelsLoading) {
      return channelsLoading[channelMMID] === 'loading';
    }
    return false;
  },
  isPollLoadingPosts(state: AVRoomState) {
    if (state.polls?.id) {
      return state.polls.pollState === 'loading';
    }
    return false;
  },
  isPollActionPermitted:
    (state: AVRoomState, getters: any, rootState: RootState, rootGetters: any) =>
    (action: BroadcastActions): boolean => {
      const isBroadcastActionPermitted = rootGetters['isBroadcastActionPermitted'];
      const currentBroadcast = rootGetters['event/currentBroadcast'];

      return isBroadcastActionPermitted(action, currentBroadcast.id);
    }
};

export type AVRoomGetters = {
  [k in keyof typeof getters]: ReturnType<(typeof getters)[k]>;
};

export default getters;
